/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc, collection, getDocs } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';


// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";

// Soft UI Dashboard PRO React example components
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Soft UI Dashboard PRO React icons
import Cube from "examples/Icons/Cube";
import Document from "examples/Icons/Document";
import Settings from "examples/Icons/Settings";

// Soft UI Dashboard PRO React base styles
import breakpoints from "assets/theme/base/breakpoints";

// Images
import burceMars from "assets/images/bruce-mars.jpg";
import curved0 from "assets/images/curved-images/curved0.jpg";
import profile from "assets/images/profile.jpg"

function Header() {
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState({ name: "", profession: "" });
  const [currentUserSubscription, setCurrentUserSubscription] = useState(null);

  const navigate = useNavigate();

  const navigatetoSubscriptions = () => {
    navigate('/applications/pricing');
  };

  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      if (user) {
        const fetchUserSubscription = async () => {
          const db = getFirestore();
          const userSubDoc = collection(db, 'customerDetails', user.uid, 'subscriptions');
          const docSnap = await getDocs(userSubDoc);
  
          let latestSubscription = null;
  
          docSnap.forEach((doc) => {
            const subscriptionData = doc.data();
            // Assuming there's a 'startDate' or similar timestamp field to determine the most recent subscription
            if (!latestSubscription || subscriptionData.current_period_start.toDate() > latestSubscription.current_period_start.toDate()) {
              if (["active", "trialing"].includes(subscriptionData.status)) {
                latestSubscription = subscriptionData;
              }
            }
          });
  
          if (latestSubscription) {
            setCurrentUserSubscription(latestSubscription);
            console.log("Most recent subscription data:", latestSubscription);
          } else {
            console.log("No current subscription found.");
            setCurrentUserSubscription(null);
          }
        };
        fetchUserSubscription();
      } else {
        setCurrentUserSubscription(null);
      }
    });
  }, []);

  const priceIdMapping = {
    Silver: ["price_1P3kdWLiDfhhXJlFnroutVz5"],
    Gold: ["price_1P3kgVLiDfhhXJlFyAPLbcjy"], 
    Platinum: ["price_1P5UcILiDfhhXJlFkZDmU8VZ", "price_1PIu0ZLiDfhhXJlFNePzCIzm"]
  };

  const isActiveSubscription = currentUserSubscription && ( currentUserSubscription.status === "active" || currentUserSubscription.status === "trialing");
  const activePlanId = isActiveSubscription ? currentUserSubscription.items[0].plan.id : null;

  const isCurrentPlan = (planName) => {
    console.log("1: ", isActiveSubscription)
    console.log("2: ", priceIdMapping[planName])
    console.log("3: ", activePlanId)
    return isActiveSubscription && priceIdMapping[planName].includes(activePlanId);
  };


  const getCurrentPlanDetails = () => {
    if (isCurrentPlan('Silver')) {
      return { label: 'Silver Subscription', badgeColor: 'success' };
    } else if (isCurrentPlan('Gold')) {
      return { label: 'Gold Subscription', badgeColor: 'success' };
    } else if (isCurrentPlan('Platinum')) {
      return { label: 'Platinum Subscription', badgeColor: 'success' };
    } else {
      return { label: 'No active plan', badgeColor: 'error' };
    }
  };

  const planDetails = getCurrentPlanDetails();
  


  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    /** 
     The event listener that's calling the handleTabsOrientation function when resizing the window.
    */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();
  
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Haal gebruikersgegevens op uit de database
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          console.log("Fetched user data:", userDoc.data());
          const userData = userDoc.data();

          setUserData({
            name: userData.firstname || "",
            lastname: userData.lastname || "",
            profession: userData.profession || "",
            profilePic: userData.profilePic || profile,
          });
          console.log("Profile Pic URL:", userData.profilePic);
        }
      }
    });
  
    return () => unsubscribe();
  }, []);

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  return (
    <SoftBox position="relative">
      <DashboardNavbar absolute light />
      <SoftBox
        display="flex"
        alignItems="center"
        position="relative"
        minHeight="18.75rem"
        borderRadius="xl"
        sx={{
          backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.info.main, 0.6),
              rgba(gradients.info.state, 0.6)
            )}, url(${curved0})`,
          backgroundSize: "cover",
          backgroundPosition: "50%",
          overflow: "hidden",
        }}
      />
      <Card
        sx={{
          backdropFilter: `saturate(200%) blur(30px)`,
          backgroundColor: ({ functions: { rgba }, palette: { white } }) => rgba(white.main, 0.8),
          boxShadow: ({ boxShadows: { navbarBoxShadow } }) => navbarBoxShadow,
          position: "relative",
          mt: -8,
          mx: 3,
          py: 2,
          px: 2,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <SoftAvatar
              src={userData.profilePic}
              alt="profile-image"
              variant="rounded"
              size="xl"
              shadow="sm"
            />
          </Grid>
          <Grid item>
            <SoftBox height="100%" mt={0.5} lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                {user ? 
                  (userData.name || userData.lastname) ? 
                    `${userData.name} ${userData.lastname}` : 
                    <span style={{ cursor: "pointer", color: "info.main" }} onClick={() => navigate('/pages/wizard')}>👋 Click to setup your profile</span>
                  : "👋 You are not logged in!"
                }
              </SoftTypography>
              <SoftTypography variant="button" color="text" fontWeight="medium">
                {user ? userData.profession : "Sign-In Required"}
              </SoftTypography>
            </SoftBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4} sx={{ ml: "auto" }}>
            <SoftBox display="flex" flexDirection="column" alignItems="flex-end">
              <SoftBox display="flex" alignItems="center" sx={{ mr: 2 }}>
                <SoftBadge
                  variant="contained"
                  color={planDetails.badgeColor}
                  size="sm"
                  badgeContent={
                    <SoftBox display="flex" alignItems="center">
                      <SoftTypography variant="button" fontWeight="medium">
                        {planDetails.label}
                      </SoftTypography>
                      <div
                        onClick={navigatetoSubscriptions}
                        style={{ cursor: 'pointer', marginLeft: '8px' }}
                      >
                        <Icon sx={{ fontWeight: "bold" }}>arrow_forward</Icon>
                      </div>
                    </SoftBox>
                  }
                  circular
                  container
                />
              </SoftBox>
            </SoftBox>
          </Grid>
        </Grid>
      </Card>
    </SoftBox>
  );
}

export default Header;
